import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Plus,
  Pencil,
  Trash2,
  X,
  Tag,
  Calendar,
  Users,
  IndianRupee,
  Sparkles,
  Search,
  Filter
} from 'lucide-react'

import { supabase } from '../../config/supabase'
import toast from 'react-hot-toast'
import OfferFormModal from './OfferFormModal'

const OffersTab = () => {
 const [offers, setOffers] = useState([])
 const [loading, setLoading] = useState(true)
 const [showCreateModal, setShowCreateModal] = useState(false)
 const [editingOffer, setEditingOffer] = useState(null)
 const [filterCategory, setFilterCategory] = useState('all')
 const [filterStatus, setFilterStatus] = useState('all')

 useEffect(() => {
 fetchOffers()
 }, [])

 const fetchOffers = async () => {
 try {
 setLoading(true)
 const { data, error } = await supabase
 .from('offers')
 .select('*')
 .order('priority', { ascending: false })
 .order('created_at', { ascending: false })

 if (error) throw error
 setOffers(data || [])
 } catch (error) {
 console.error('Error fetching offers:', error)
 toast.error('Failed to load offers')
 } finally {
 setLoading(false)
 }
 }

 const handleDelete = async (offerId) => {
 if (!confirm('Are you sure you want to delete this offer?')) return

 try {
 const { error } = await supabase
 .from('offers')
 .delete()
 .eq('id', offerId)

 if (error) throw error
 toast.success('Offer deleted successfully!')
 fetchOffers()
 } catch (error) {
 console.error('Error deleting offer:', error)
 toast.error('Failed to delete offer')
 }
 }

 const handleToggleActive = async (offer) => {
 try {
 const { error } = await supabase
 .from('offers')
 .update({ is_active: !offer.is_active })
 .eq('id', offer.id)

 if (error) throw error
 toast.success(`Offer ${!offer.is_active ? 'activated' : 'deactivated'}!`)
 fetchOffers()
 } catch (error) {
 console.error('Error toggling offer status:', error)
 toast.error('Failed to update offer status')
 }
 }

 const handleEdit = (offer) => {
 setEditingOffer(offer)
 setShowCreateModal(true)
 }

 const filteredOffers = offers.filter(offer => {
 const categoryMatch = filterCategory === 'all' || offer.offer_category === filterCategory
 const statusMatch = filterStatus === 'all' || 
 (filterStatus === 'active' && offer.is_active) || 
 (filterStatus === 'inactive' && !offer.is_active)
 return categoryMatch && statusMatch
 })

 const getUserTypeLabel = (type) => {
 const labels = {
 'new_user': '🆕 New Users',
 'existing_user': '👤 Existing Users',
 'guest_user': '👥 Guest Users',
 'all': '🌍 All Users',
 'festive': '🎉 Festive (All Users)'
 }
 return labels[type] || type
 }

 const getCategoryLabel = (category) => {
 const labels = {
 'general': 'General',
 'festive': '🎊 Festive',
 'seasonal': '🌸 Seasonal',
 'loyalty': '⭐ Loyalty',
 'welcome': '👋 Welcome',
 'winback': '🔄 Win-back'
 }
 return labels[category] || category
 }

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
 <div>
 <h2 className="text-2xl font-semibold text-slate-900">Offers Management</h2>
 <p className="text-neutral-600 mt-1">Create and manage promotional offers for customers</p>
 </div>
 <motion.button
 whileHover={{ scale: 1.05 }}
 whileTap={{ scale: 0.95 }}
 onClick={() => {
 setEditingOffer(null)
 setShowCreateModal(true)
 }}
 className="px-6 py-3 bg-indigo-600 text-slate-900 rounded-xl font-semibold shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
 >
 <Plus className="h-5 w-5" />
 Create New Offer
 </motion.button>
 </div>

 {/* Filters */}
 <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm shadow-md">
 <div className="flex flex-wrap gap-3">
 <select
 value={filterCategory}
 onChange={(e) => setFilterCategory(e.target.value)}
 className="px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 >
 <option value="all">All Categories</option>
 <option value="general">General</option>
 <option value="festive">Festive</option>
 <option value="seasonal">Seasonal</option>
 <option value="loyalty">Loyalty</option>
 <option value="welcome">Welcome</option>
 <option value="winback">Win-back</option>
 </select>

 <select
 value={filterStatus}
 onChange={(e) => setFilterStatus(e.target.value)}
 className="px-4 py-2 border border-slate-100 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
 >
 <option value="all">All Status</option>
 <option value="active">Active</option>
 <option value="inactive">Inactive</option>
 </select>
 </div>
 </div>

 {/* Offers Grid */}
 {loading ? (
 <div className="text-center py-12">
 <div className="animate-spin rounded-full h-12 w-12 border-b border-slate-100 mx-auto"></div>
 <p className="mt-4 font-medium text-neutral-600">Loading offers...</p>
 </div>
 ) : filteredOffers.length === 0 ? (
 <div className="bg-white rounded-xl p-12 text-center border border-slate-100 shadow-sm">
 <Tag className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
 <h3 className="text-xl font-semibold text-slate-900 mb-2">No Offers Found</h3>
 <p className="text-neutral-600">Create your first offer to get started!</p>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
 {filteredOffers.map((offer) => (
 <motion.div
 key={offer.id}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300"
 >
 {/* Offer Header */}
 <div className="flex justify-between items-start mb-4">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2 flex-wrap">
 <span className={`px-3 py-1 rounded-full text-xs font-semibold border border-slate-100 ${
 offer.is_active ? 'bg-green-400' : 'bg-neutral-300'
 }`}>
 {offer.is_active ? 'Active' : 'Inactive'}
 </span>
 <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 border border-slate-100">
 {getCategoryLabel(offer.offer_category)}
 </span>
 </div>
 <h3 className="text-lg font-semibold text-slate-900">{offer.offer_name}</h3>
 <p className="text-sm font-medium text-amber-600 mt-1">Code: {offer.offer_code}</p>
 </div>
 </div>

 {/* Offer Details */}
 <div className="space-y-3 mb-4">
 <div className="flex items-center gap-2 text-sm">
 <Tag className="h-4 w-4 text-neutral-600" />
 <span className="font-medium">
 {offer.discount_type === 'percentage' 
 ? `${offer.discount_value}% OFF` 
 : `₹${offer.discount_value} OFF`}
 </span>
 {offer.max_discount_amount && (
 <span className="text-neutral-600">(Max: ₹{offer.max_discount_amount})</span>
 )}
 </div>

 {offer.min_order_amount > 0 && (
 <div className="flex items-center gap-2 text-sm">
 <IndianRupee className="h-4 w-4 text-neutral-600" />
 <span className="text-neutral-700">Min Order: ₹{offer.min_order_amount}</span>
 </div>
 )}

 <div className="flex items-center gap-2 text-sm">
 <Users className="h-4 w-4 text-neutral-600" />
 <span className="text-neutral-700">{getUserTypeLabel(offer.target_user_type)}</span>
 </div>

 <div className="flex items-center gap-2 text-sm">
 <Calendar className="h-4 w-4 text-neutral-600" />
 <span className="text-neutral-700">
 {new Date(offer.valid_from).toLocaleDateString()} - {' '}
 {offer.valid_until ? new Date(offer.valid_until).toLocaleDateString() : 'No expiry'}
 </span>
 </div>

 <div className="text-sm text-neutral-600">
 <span className="font-medium">Usage:</span> {offer.usage_limit_per_customer} per customer
 {offer.total_usage_limit && ` • ${offer.total_usage_limit} total`}
 </div>

 {offer.description && (
 <p className="text-sm text-neutral-600 line-clamp-2">{offer.description}</p>
 )}
 </div>

 {/* Actions */}
 <div className="flex gap-2 pt-4 border-t-2 border-slate-200">
 <button
 onClick={() => handleToggleActive(offer)}
 className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm border border-slate-100 transition-colors ${
 offer.is_active 
 ? 'bg-neutral-200 hover:bg-neutral-300' 
 : 'bg-green-400 hover:bg-emerald-500'
 }`}
 >
 {offer.is_active ? 'Deactivate' : 'Activate'}
 </button>
 <button
 onClick={() => handleEdit(offer)}
 className="px-3 py-2 bg-blue-400 hover:bg-indigo-500 rounded-lg font-medium text-sm border border-slate-100 transition-colors"
 >
 <Pencil className="h-4 w-4" />
 </button>
 <button
 onClick={() => handleDelete(offer.id)}
 className="px-3 py-2 bg-red-400 hover:bg-red-500 rounded-lg font-medium text-sm border border-slate-100 transition-colors"
 >
 <Trash2 className="h-4 w-4" />
 </button>
 </div>
 </motion.div>
 ))}
 </div>
 )}

 {/* Create/Edit Modal */}
 {showCreateModal && (
 <OfferFormModal
 editingOffer={editingOffer}
 onClose={() => {
 setShowCreateModal(false)
 setEditingOffer(null)
 }}
 onSuccess={() => {
 setShowCreateModal(false)
 setEditingOffer(null)
 fetchOffers()
 }}
 />
 )}
 </div>
 )
}

export default OffersTab
